import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Alert,
  Tabs,
  Tab,
  InputGroup,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch, FaRobot } from 'react-icons/fa';

import { selectAuthUserId } from '../redux/authSlice';
import { fetchCoursesByUserId } from '../services/supabaseClient/courseService';
import {
  createProject,
  updateProject,
  hardDeleteProject,
  fetchUserProjects,
  startProjectTimer,
  pauseProjectTimer,
  resumeProjectTimer,
  stopProjectTimer,
} from '../services/supabaseClient/projectService';
import ProjectCard from '../components/ui/ProjectCard';
import FancyButton from '../components/ui/fancyButton';
import FormField from '../components/ui/FormField';
import TextInput from '../components/ui/TextInput';
import TextArea from '../components/ui/TextArea';
import Select from '../components/ui/Select';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { ProjectGenerationService } from '../services/llmClient';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const userId = useSelector(selectAuthUserId);
  const navigate = useNavigate();

  // State
  const [projects, setProjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    unit_id: '',
    difficulty_level: 'intermediate',
    estimated_hours: 10,
    project_type: 'application',
    technologies: '',
    sharing_platforms: [],
  });

  // Load courses for linking
  useEffect(() => {
    const loadCourses = async () => {
      if (userId) {
        const { data, error } = await fetchCoursesByUserId(userId);
        if (!error && data) {
          setCourses(data);
        }
      }
    };
    loadCourses();
  }, [userId]);

  // Update selected course when course_id changes
  useEffect(() => {
    if (formData.course_id) {
      const course = courses.find((c) => c.id === formData.course_id);
      setSelectedCourse(course);
    } else {
      setSelectedCourse(null);
      setFormData((prev) => ({ ...prev, unit_id: '' }));
    }
  }, [formData.course_id, courses]);

  // Mock data for development
  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await fetchUserProjects();

      if (fetchError) {
        setError('Failed to load projects');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Error loading projects:', fetchError);
      } else {
        setProjects(data || []);
        // eslint-disable-next-line no-console
        console.log('[ProjectsPage] Loaded projects:', data?.length || 0);
      }
    } catch (err) {
      setError('Failed to load projects');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      loadData();
      return;
    }

    const filtered = projects.filter(
      (project) =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.technologies?.some((tech) => tech.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setProjects(filtered);
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      setCreatingProject(true);
      const technologiesArray = formData.technologies
        ? formData.technologies
            .split(',')
            .map((tech) => tech.trim())
            .filter((t) => t)
        : [];

      const projectData = {
        title: formData.title,
        description: formData.description,
        course_id: formData.course_id || null,
        unit_id: formData.unit_id || null,
        difficulty_level: formData.difficulty_level,
        estimated_hours: parseInt(formData.estimated_hours) || 10,
        project_type: formData.project_type,
        technologies: technologiesArray,
      };

      // Find selected course/unit for richer AI context
      const selectedCourseObj = projectData.course_id
        ? courses.find((c) => c.id === projectData.course_id)
        : null;
      const selectedUnit =
        selectedCourseObj && projectData.unit_id
          ? (selectedCourseObj.units || []).find((u) => u.id === projectData.unit_id)
          : null;

      // Call LLM to generate a detailed project plan with milestones
      const aiResponse = await ProjectGenerationService.generateProject({
        courseId: projectData.course_id || null,
        unitId: projectData.unit_id || null,
        courseTitle: selectedCourseObj?.title,
        courseDescription: selectedCourseObj?.description,
        difficultyLevel: projectData.difficulty_level,
        unitTitle: selectedUnit?.title,
        unitObjective: selectedUnit?.objective,
        topicsCovered: selectedUnit?.topics || [],
        studentProfile: {},
        estimatedHours: projectData.estimated_hours,
        projectType: projectData.project_type,
        // Custom project context
        projectTitle: projectData.title,
        projectDescription: projectData.description,
        technologies: projectData.technologies,
        // For now, leave learningObjectives, prerequisites, deliverables empty;
        // the LLM will infer them from description and course/unit context.
      });

      if (!aiResponse?.success) {
        setError(aiResponse?.error || 'Failed to generate project with AI');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Error generating project with AI:', aiResponse?.error);
        return;
      }

      const aiProject = aiResponse.project;

      const dbProjectData = {
        title: aiProject.title || projectData.title,
        description: aiProject.description || projectData.description,
        course_id: projectData.course_id,
        unit_id: projectData.unit_id,
        project_type: aiProject.project_type || projectData.project_type,
        technologies:
          Array.isArray(aiProject.technologies) && aiProject.technologies.length > 0
            ? aiProject.technologies
            : projectData.technologies,
        learning_objectives: aiProject.learning_objectives || [],
        prerequisites: aiProject.prerequisites || [],
        deliverables: aiProject.deliverables || [],
        sharing_platforms: aiProject.sharing_platforms || [],
        ai_generated: true,
        status: 'not_started',
        progress_percentage: 0,
        generation_prompt: aiProject.generation_prompt || null,
        milestones: aiProject.milestones || [],
      };

      const { data, error: addError } = await createProject(dbProjectData);

      if (addError) {
        setError(addError.message || 'Failed to create project');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Error creating project:', addError);
      } else {
        // eslint-disable-next-line no-console
        console.log('[ProjectsPage] Project created successfully:', data);
        setShowAddModal(false);
        resetForm();
        loadData();
      }
    } catch (err) {
      setError('An error occurred while creating the project');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Exception:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const technologiesArray = formData.technologies
        ? formData.technologies
            .split(',')
            .map((tech) => tech.trim())
            .filter((t) => t)
        : [];

      const updates = {
        title: formData.title,
        description: formData.description,
        course_id: formData.course_id || null,
        unit_id: formData.unit_id || null,
        difficulty_level: formData.difficulty_level,
        estimated_hours: parseInt(formData.estimated_hours) || 10,
        project_type: formData.project_type,
        technologies: technologiesArray,
      };

      const { data, error: editError } = await updateProject(formData.id, updates);

      if (editError) {
        setError(editError.message || 'Failed to update project');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Error updating project:', editError);
      } else {
        // eslint-disable-next-line no-console
        console.log('[ProjectsPage] Project updated successfully:', data);
        setShowEditModal(false);
        resetForm();
        loadData();
      }
    } catch (err) {
      setError('An error occurred while updating the project');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Exception:', err);
    }
  };

  const handleDeleteProject = async (project) => {
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${project.title}"? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const { error: deleteError } = await hardDeleteProject(project.id);

      if (deleteError) {
        setError(deleteError.message || 'Failed to delete project');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Error deleting project:', deleteError);
      } else {
        // eslint-disable-next-line no-console
        console.log('[ProjectsPage] Project deleted permanently:', project.id);
        loadData();
      }
    } catch (err) {
      setError('An error occurred while deleting the project');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Exception:', err);
      // }
    }
  };

  const handleViewProject = (project) => {
    navigate(`/projects/${project.id}`);
  };

  const handleEditClick = (project) => {
    setFormData({
      title: project.title,
      description: project.description || '',
      course_id: project.course_id || '',
      unit_id: project.unit_id || '',
      difficulty_level: project.difficulty_level || 'intermediate',
      estimated_hours: project.estimated_hours || 10,
      project_type: project.project_type || 'application',
      technologies: project.technologies ? project.technologies.join(', ') : '',
      sharing_platforms: project.sharing_platforms || [],
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course_id: '',
      unit_id: '',
      difficulty_level: 'intermediate',
      estimated_hours: 10,
      project_type: 'application',
      technologies: '',
      sharing_platforms: [],
    });
  };

  // Timer handlers
  const handleTimerStart = async (projectId) => {
    try {
      const { error: timerError } = await startProjectTimer(projectId);
      if (timerError) {
        setError('Failed to start timer');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Timer start error:', timerError);
      } else {
        loadData(); // Refresh projects to get updated timer state
      }
    } catch (err) {
      setError('Failed to start timer');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Timer start exception:', err);
    }
  };

  const handleTimerPause = async (projectId, elapsedSeconds) => {
    try {
      const { error: timerError } = await pauseProjectTimer(projectId, elapsedSeconds);
      if (timerError) {
        setError('Failed to pause timer');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Timer pause error:', timerError);
      }
    } catch (err) {
      setError('Failed to pause timer');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Timer pause exception:', err);
    }
  };

  const handleTimerResume = async (projectId) => {
    try {
      const { error: timerError } = await resumeProjectTimer(projectId);
      if (timerError) {
        setError('Failed to resume timer');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Timer resume error:', timerError);
      }
    } catch (err) {
      setError('Failed to resume timer');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Timer resume exception:', err);
    }
  };

  const handleTimerStop = async (projectId, elapsedSeconds) => {
    try {
      const { error: timerError } = await stopProjectTimer(projectId, elapsedSeconds);
      if (timerError) {
        setError('Failed to stop timer');
        // eslint-disable-next-line no-console
        console.error('[ProjectsPage] Timer stop error:', timerError);
      } else {
        loadData(); // Refresh projects to get updated timer state
      }
    } catch (err) {
      setError('Failed to stop timer');
      // eslint-disable-next-line no-console
      console.error('[ProjectsPage] Timer stop exception:', err);
    }
  };

  const getDisplayProjects = () => {
    switch (activeTab) {
      case 'in_progress':
        return projects.filter((p) => p.status === 'in_progress');
      case 'completed':
        return projects.filter((p) => p.status === 'completed');
      case 'ai':
        return projects.filter((p) => p.ai_generated);
      case 'all':
      default:
        return projects;
    }
  };

  const displayProjects = getDisplayProjects();
  const inProgressCount = projects.filter((p) => p.status === 'in_progress').length;
  const completedCount = projects.filter((p) => p.status === 'completed').length;
  const aiGeneratedCount = projects.filter((p) => p.ai_generated).length;

  if (loading && projects.length === 0) {
    return (
      <Container className="projects-page py-5">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="projects-page py-4">
      <div className="projects-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h2 className="projects-title">
              <i className="fa-solid fa-diagram-project me-2" />
              Projects
            </h2>
            <p className="text-muted">Build real-world projects from your courses</p>
          </div>
          <div className="d-flex gap-2">
            <FancyButton variant="secondary" onClick={() => setShowAddModal(true)}>
              <FaPlus className="me-2" />
              Create Custom
            </FancyButton>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="projects-search mb-4">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search projects by title, description, or technology..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button variant="outline-primary" onClick={handleSearch}>
            <FaSearch />
          </Button>
        </InputGroup>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 projects-tabs">
        <Tab eventKey="all" title={`All Projects (${projects.length})`}>
          {displayProjects.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="fa-solid fa-diagram-project fa-3x mb-3 text-muted" />
              <h4>No projects yet</h4>
              <p className="text-muted">Create your first project or generate one with AI</p>
              <FancyButton variant="primary" onClick={() => setShowGenerateModal(true)}>
                <FaRobot className="me-2" />
                Generate Project
              </FancyButton>
            </div>
          ) : (
            <Row>
              {displayProjects.map((project) => (
                <Col key={project.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                  <ProjectCard
                    project={project}
                    onView={handleViewProject}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteProject}
                    onTimerStart={handleTimerStart}
                    onTimerPause={handleTimerPause}
                    onTimerResume={handleTimerResume}
                    onTimerStop={handleTimerStop}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        <Tab eventKey="in_progress" title={`In Progress (${inProgressCount})`}>
          {displayProjects.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="fa-solid fa-spinner fa-3x mb-3 text-muted" />
              <h4>No projects in progress</h4>
              <p className="text-muted">Start working on a project to see it here</p>
            </div>
          ) : (
            <Row>
              {displayProjects.map((project) => (
                <Col key={project.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                  <ProjectCard
                    project={project}
                    onView={handleViewProject}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteProject}
                    onTimerStart={handleTimerStart}
                    onTimerPause={handleTimerPause}
                    onTimerResume={handleTimerResume}
                    onTimerStop={handleTimerStop}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        <Tab eventKey="completed" title={`Completed (${completedCount})`}>
          {displayProjects.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="fa-solid fa-check-circle fa-3x mb-3 text-muted" />
              <h4>No completed projects</h4>
              <p className="text-muted">Complete a project to see it here</p>
            </div>
          ) : (
            <Row>
              {displayProjects.map((project) => (
                <Col key={project.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                  <ProjectCard
                    project={project}
                    onView={handleViewProject}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteProject}
                    onTimerStart={handleTimerStart}
                    onTimerPause={handleTimerPause}
                    onTimerResume={handleTimerResume}
                    onTimerStop={handleTimerStop}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Tab>

        <Tab eventKey="ai" title={`AI (${aiGeneratedCount})`}>
          {displayProjects.length === 0 ? (
            <div className="empty-state text-center py-5">
              <FaRobot size={48} className="mb-3 text-muted" />
              <h4>No AI proj yet</h4>
              <p className="text-muted">Gen a proj with AI to get started</p>
            </div>
          ) : (
            <Row>
              {displayProjects.map((project) => (
                <Col key={project.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                  <ProjectCard
                    project={project}
                    onView={handleViewProject}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteProject}
                    onTimerStart={handleTimerStart}
                    onTimerPause={handleTimerPause}
                    onTimerResume={handleTimerResume}
                    onTimerStop={handleTimerStop}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Tab>
      </Tabs>

      {/* Add Project Modal */}
      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          resetForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Custom Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddProject}>
            <FormField label="Project Title" htmlFor="title" required>
              <TextInput
                id="title"
                name="title"
                placeholder="Enter project title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Description" htmlFor="description" required>
              <TextArea
                id="description"
                name="description"
                rows={4}
                placeholder="Describe your project"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </FormField>

            <FormField
              label="Link to Course (Optional)"
              htmlFor="course_id"
              helpText="Associate this project with a course"
            >
              <Select
                id="course_id"
                name="course_id"
                value={formData.course_id}
                onChange={(e) => setFormData({ ...formData, course_id: e.target.value })}
                options={[
                  { value: '', label: '-- No Course --' },
                  ...courses.map((course) => ({
                    value: course.id,
                    label: course.title,
                  })),
                ]}
              />
            </FormField>

            <FormField
              label="Link to Unit (Optional)"
              htmlFor="unit_id"
              helpText={formData.course_id ? 'Select a specific unit' : 'Select a course first'}
            >
              <Select
                id="unit_id"
                name="unit_id"
                value={formData.unit_id}
                onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })}
                disabled={!formData.course_id}
                options={[
                  { value: '', label: '-- No Unit --' },
                  ...(selectedCourse?.units || []).map((unit) => ({
                    value: unit.id,
                    label: unit.title,
                  })),
                ]}
              />
            </FormField>

            <FormField label="Difficulty Level" htmlFor="difficulty_level" required>
              <Select
                id="difficulty_level"
                name="difficulty_level"
                value={formData.difficulty_level}
                onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                options={[
                  { value: 'beginner', label: 'Beginner' },
                  { value: 'intermediate', label: 'Intermediate' },
                  { value: 'advanced', label: 'Advanced' },
                ]}
                required
              />
            </FormField>

            <FormField label="Estimated Hours" htmlFor="estimated_hours" required>
              <TextInput
                id="estimated_hours"
                name="estimated_hours"
                type="number"
                min="1"
                placeholder="10"
                value={formData.estimated_hours}
                onChange={(e) =>
                  setFormData({ ...formData, estimated_hours: parseInt(e.target.value) })
                }
                required
              />
            </FormField>

            <FormField label="Technologies" htmlFor="technologies" helpText="Comma-separated">
              <TextInput
                id="technologies"
                name="technologies"
                placeholder="e.g., React, Node.js, MongoDB"
                value={formData.technologies}
                onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
              />
            </FormField>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                disabled={creatingProject}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={creatingProject}>
                {creatingProject ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Generating milestones...
                  </>
                ) : (
                  'Create Project'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Project Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          resetForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditProject}>
            <FormField label="Project Title" htmlFor="edit_title" required>
              <TextInput
                id="edit_title"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Description" htmlFor="edit_description" required>
              <TextArea
                id="edit_description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </FormField>

            <FormField label="Difficulty Level" htmlFor="edit_difficulty_level" required>
              <Select
                id="edit_difficulty_level"
                name="difficulty_level"
                value={formData.difficulty_level}
                onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                options={[
                  { value: 'beginner', label: 'Beginner' },
                  { value: 'intermediate', label: 'Intermediate' },
                  { value: 'advanced', label: 'Advanced' },
                ]}
                required
              />
            </FormField>

            <FormField label="Technologies" htmlFor="edit_technologies" helpText="Comma-separated">
              <TextInput
                id="edit_technologies"
                name="technologies"
                value={formData.technologies}
                onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
              />
            </FormField>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* AI Generate Modal - Placeholder */}
      <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaRobot className="me-2" />
            Generate AI Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>Coming Soon!</strong>
          </Alert>
          <p className="text-muted">
            This feature will use AI to generate personalized projects based on your course progress
            and learning goals.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGenerateModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProjectsPage;
